import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '@/api/media';
import type { GenerateInput, MediaModel } from '@/types/media';

export const MEDIA_MODELS_KEY = ['media-models'] as const;
export const GENERATIONS_KEY = ['media-generations'] as const;

export function useMediaModels() {
  return useQuery({ queryKey: MEDIA_MODELS_KEY, queryFn: api.listMediaModels });
}
export function useCreateMediaModel() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: Partial<MediaModel>) => api.createMediaModel(b), onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_MODELS_KEY }) });
}
export function useUpdateMediaModel() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ model, b }: { model: string; b: Partial<MediaModel> }) => api.updateMediaModel(model, b), onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_MODELS_KEY }) });
}
export function useDeleteMediaModel() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (model: string) => api.deleteMediaModel(model), onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_MODELS_KEY }) });
}

export function useGenerations(q: Parameters<typeof api.listGenerations>[0]) {
  return useQuery({ queryKey: [...GENERATIONS_KEY, q], queryFn: () => api.listGenerations(q) });
}
export function useGenerate() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: GenerateInput) => api.generate(b), onSuccess: () => qc.invalidateQueries({ queryKey: GENERATIONS_KEY }) });
}
// Poll a single running generation until terminal (mirrors useJobPoll).
// NOTE: the caller (GeneratePanel) watches for the terminal transition and
// invalidates GENERATIONS_KEY so async results appear in the library.
export function useGenerationPoll(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['media-generation', id],
    queryFn: () => api.getGeneration(id!),
    enabled: !!id && enabled,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'succeeded' || s === 'failed' || s === 'canceled' ? false : 2000;
    },
  });
}
export function useCancelGeneration() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.cancelGeneration(id), onSuccess: () => qc.invalidateQueries({ queryKey: GENERATIONS_KEY }) });
}
export function usePatchTags() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, tags }: { id: string; tags: string[] }) => api.patchGenerationTags(id, tags), onSuccess: () => qc.invalidateQueries({ queryKey: GENERATIONS_KEY }) });
}
export function useDeleteGeneration() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.deleteGeneration(id), onSuccess: () => qc.invalidateQueries({ queryKey: GENERATIONS_KEY }) });
}
